// ═══════════════════════════════════════════════════════════
// FOIA — GAB Document Storage API
//
// GAB stores files separately from table records.
// The flow for every file in this app:
//
//   UPLOAD
//   1. POST FormData to /v2/apps/{APP_ID}/documents
//   2. Receive { id, filename, fileSize, mimeType }
//   3. Build a storageUrl marker: |||DOCID|||{uuid}|||
//   4. Save that marker string into the relevant TEXT field
//      on the table record (documents.storageUrl, etc.)
//
//   DOWNLOAD
//   1. Read the TEXT field, extract the uuid via extractDocId()
//   2. GET /v2/apps/{APP_ID}/documents/{id}/download-url
//   3. Receive { url } — signed S3 URL, valid ~10 min
//   4. Redirect user or fetch from that URL
//
// Public portal downloads (anonymous): route through /api/v2proxy
// which auto-injects the admin token. No client-side token needed.
//
// !! NEVER expose signed S3 URLs to records that are not
// !! marked isReleased=true. Check status before generating URLs.
// ═══════════════════════════════════════════════════════════

const APP_ID = import.meta.env.VITE_V2_APP_ID;

// /api/v2proxy → GAB API (Vite proxy in dev, Cloudflare Pages Function in prod)
// Auth is handled server-side via cookies — no client-side token injection needed.
const PROXY_BASE = '/api/v2proxy';

// ── GAB document marker format ───────────────────────────
// GAB identifies uploaded files by UUID. We store the UUID in a
// TEXT field using this sentinel format so we can distinguish
// "no file" from an actual URL vs a GAB document reference.

const DOC_MARKER_PREFIX = '|||DOCID|||';
const DOC_MARKER_SUFFIX = '|||';

export function buildStorageUrl(docId: string): string {
  return `${DOC_MARKER_PREFIX}${docId}${DOC_MARKER_SUFFIX}`;
}

export function extractDocId(storageUrl: string | null | undefined): string | null {
  if (!storageUrl) return null;
  const start = storageUrl.indexOf(DOC_MARKER_PREFIX);
  if (start === -1) return null;
  const inner = storageUrl.slice(start + DOC_MARKER_PREFIX.length);
  const end = inner.indexOf(DOC_MARKER_SUFFIX);
  return end === -1 ? inner : inner.slice(0, end);
}

export function hasDocument(storageUrl: string | null | undefined): boolean {
  return extractDocId(storageUrl) !== null;
}

// ── Local file blob cache ─────────────────────────────────
// Caches the original File object in memory after upload so the
// Redaction Editor can load the PDF without needing a GAB download-url.
// Keyed by GAB document UUID. Auto-expires after 30 minutes.
// This is necessary because GAB's download-url endpoint requires the
// attachmentId to be written to an ATTACHMENT field, which FOIA doesn't
// use (we store doc IDs in TEXT fields). Until the GAB app is
// reconfigured with ATTACHMENT fields, this cache is the fallback.

const _fileCache = new Map<string, File>();

export function cacheFileForDoc(docId: string, file: File): void {
  _fileCache.set(docId, file);
  setTimeout(() => _fileCache.delete(docId), 30 * 60 * 1000);
}

export function getCachedFile(docId: string): File | null {
  return _fileCache.get(docId) ?? null;
}

// ── Types ────────────────────────────────────────────────

export interface GabDocumentRef {
  id: string;           // documentId — use for download-url calls
  attachmentId: string; // attachmentId — write to ATTACHMENT field to confirm upload
  filename: string;
  size: number;         // GAB returns "size", not "fileSize"
  url?: string;
  uploadedAt?: string;
}

export interface SignedUrl {
  url: string;
  expiresAt?: string;
}

// ── Upload ───────────────────────────────────────────────

/**
 * Upload a file to GAB document storage.
 *
 * GAB uses a presigned S3 URL flow:
 *   1. POST /documents (JSON metadata) → get presignedUrl + documentId
 *   2. PUT file bytes to presignedUrl via /api/s3upload server-side proxy
 *      (avoids S3 CORS restriction on direct browser-to-S3 PUTs)
 *
 * For anonymous uploads (public portal): the v2proxy injects the admin token
 * for step 1. Step 2 goes directly to /api/s3upload (no auth needed).
 *
 * Returns a GabDocumentRef. Call buildStorageUrl(ref.id) to get
 * the marker string to store in the TEXT field.
 */
export async function uploadDocument(
  file: File,
  opts?: { anonymous?: boolean; onProgress?: (pct: number) => void; tableKey?: string },
): Promise<GabDocumentRef> {
  // ── Step 1: Request upload slot from GAB ─────────────────
  // Auth is handled by the proxy via cookie — no manual header needed.
  console.log(`[uploadDocument] requesting slot for "${file.name}" (${file.size} bytes, type: ${file.type})`);

  const slotRes = await fetch(`${PROXY_BASE}/v2/apps/${APP_ID}/documents`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(opts?.tableKey ? { tableKey: opts.tableKey } : {}),
      files: [{
        fileName:    file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize:    file.size,
      }],
    }),
  });

  if (!slotRes.ok) {
    const body = await slotRes.text().catch(() => '');
    throw new Error(`Upload failed: ${slotRes.status} — ${body}`);
  }

  const slotData = await slotRes.json() as {
    attachmentId?: string;
    documents: Array<{
      id: string;
      presignedUrl: string;
      method?: string;
      headers?: Record<string, string>;
    }>;
  };

  console.log(`[uploadDocument] slot response — attachmentId: ${slotData.attachmentId}, documentId: ${slotData.documents?.[0]?.id}`);

  const doc = slotData.documents?.[0];
  if (!doc?.presignedUrl) throw new Error('No presigned URL returned from GAB');

  // ── Step 2: PUT file bytes to S3 via /api/s3upload proxy ────
  // Always route through /api/s3upload (Cloudflare Pages Function in prod,
  // Vite dev middleware in dev). Direct browser-to-S3 PUTs don't trigger
  // GAB's S3 event notifications, leaving documents in a 409 "pending" state.
  const extraHeaders: Record<string, string> = doc.headers ?? {};

  const s3Form = new FormData();
  s3Form.append('file', file);
  s3Form.append('presignedUrl', doc.presignedUrl);
  if (Object.keys(extraHeaders).length > 0) {
    s3Form.append('headersJson', JSON.stringify(extraHeaders));
  }

  console.log(`[uploadDocument] PUT to S3 via /api/s3upload (docId: ${doc.id}, presignedUrl: ${doc.presignedUrl.slice(0, 80)}...)`);

  if (opts?.onProgress) {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/s3upload');
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) opts.onProgress!(Math.round((e.loaded / e.total) * 100));
      });
      xhr.onload = () => {
        console.log(`[uploadDocument] /api/s3upload XHR status: ${xhr.status} response: ${xhr.responseText.slice(0, 200)}`);
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`S3 upload failed: ${xhr.status} — ${xhr.responseText}`));
      };
      xhr.onerror = () => reject(new Error('Network error during S3 upload'));
      xhr.send(s3Form);
    });
    opts.onProgress(100);
  } else {
    const s3Res = await fetch('/api/s3upload', { method: 'POST', body: s3Form });
    const s3Body = await s3Res.text().catch(() => '');
    console.log(`[uploadDocument] /api/s3upload fetch status: ${s3Res.status} response: ${s3Body.slice(0, 200)}`);
    if (!s3Res.ok) {
      throw new Error(`S3 upload failed: ${s3Res.status} — ${s3Body}`);
    }
  }

  return {
    id:           doc.id,
    attachmentId: slotData.attachmentId ?? '',
    filename:     file.name,
    size:         file.size,
  };
}

/**
 * Upload multiple files, returning results in the same order.
 * Failures in one file do not abort the others.
 */
export async function uploadDocuments(
  files: File[],
  opts?: { anonymous?: boolean },
): Promise<Array<GabDocumentRef | Error>> {
  return Promise.all(
    files.map(f =>
      uploadDocument(f, opts).catch(e => (e instanceof Error ? e : new Error(String(e))))
    )
  );
}

// ── Download URL ─────────────────────────────────────────

/**
 * Get a signed S3 download URL for a GAB document.
 *
 * Polls up to maxAttempts times with delayMs between each — GAB
 * occasionally needs a moment after upload before the URL is ready.
 *
 * For public portal (released documents): pass anonymous: true.
 * The v2proxy will inject the admin token server-side.
 *
 * !! Only call this for documents you have verified are releasable.
 * !! For staff-only documents, ensure the caller has auth.
 */
export async function getDownloadUrl(
  docId: string,
  opts?: { anonymous?: boolean; maxAttempts?: number; delayMs?: number },
): Promise<string> {
  const maxAttempts = opts?.maxAttempts ?? 8;
  const delayMs = opts?.delayMs ?? 2000;

  const path = `${PROXY_BASE}/v2/apps/${APP_ID}/documents/${docId}/download-url`;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const res = await fetch(path, { credentials: 'include' });

    if (res.ok) {
      const data = await res.json() as SignedUrl & { presignedUrl?: string; download_url?: string };
      // GAB returns presignedUrl; older responses may use url or download_url
      const signed = data.presignedUrl ?? data.url ?? data.download_url;
      if (signed) return signed;
    }

    if (!res.ok) {
      const bodyText = await res.clone().text().catch(() => '');

      // 409 with DocumentNotReadyError = S3 event not yet confirmed — retry
      // 500 = similar transient state — also retry
      const isTransient =
        res.status === 500 ||
        (res.status === 409 && bodyText.includes('DocumentNotReadyError'));

      if (!isTransient) {
        // Permanent error (403, 404, or 409 with a different reason)
        throw new Error(`Download URL unavailable (${res.status}): ${bodyText}`);
      }

      if (attempt === 1) {
        console.warn(`[getDownloadUrl] ${res.status} (attempt ${attempt}/${maxAttempts}) — waiting for S3 confirmation`);
      }
    }

    if (attempt < maxAttempts) {
      await new Promise(r => setTimeout(r, delayMs));
    }
  }

  throw new Error(`Could not get download URL for document ${docId} after ${maxAttempts} attempts (upload may not have completed)`);
}

/**
 * Convenience: extract docId from a storageUrl field and fetch its signed URL.
 * Returns null if the field has no document reference.
 */
export async function getDownloadUrlFromField(
  storageUrlField: string | null | undefined,
  opts?: { anonymous?: boolean },
): Promise<string | null> {
  const docId = extractDocId(storageUrlField);
  if (!docId) return null;
  return getDownloadUrl(docId, opts);
}

// ── Delete ───────────────────────────────────────────────

/**
 * Delete a document from GAB storage.
 * This is irreversible — only call for truly unwanted documents.
 * For FOIA compliance: prefer keeping originals. Only delete
 * temporary/draft files or requester uploads that were rejected.
 */
export async function deleteDocument(docId: string): Promise<void> {
  const res = await fetch(`${PROXY_BASE}/v2/apps/${APP_ID}/documents/${docId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!res.ok && res.status !== 404) {
    throw new Error(`Delete failed: ${res.status}`);
  }
}

// ── Inline preview ───────────────────────────────────────

/**
 * Check if a MIME type is previewable in the browser.
 * Used to decide whether to show "View" vs "Download" buttons.
 */
export function isPreviewable(mimeType: string): boolean {
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    mimeType === 'text/plain'
  );
}

/**
 * Format a file size in bytes to a human-readable string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Validation ───────────────────────────────────────────

const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/gif',
  'application/zip',
  'application/x-zip-compressed',
]);

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB per file

export interface FileValidationError {
  file: File;
  reason: 'size' | 'type';
  message: string;
}

export function validateFiles(files: File[]): {
  valid: File[];
  errors: FileValidationError[];
} {
  const valid: File[] = [];
  const errors: FileValidationError[] = [];

  for (const file of files) {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      errors.push({ file, reason: 'size', message: `${file.name} exceeds the 50 MB limit (${formatFileSize(file.size)})` });
    } else if (!ALLOWED_MIME_TYPES.has(file.type)) {
      errors.push({ file, reason: 'type', message: `${file.name} is not an allowed file type (${file.type || 'unknown'})` });
    } else {
      valid.push(file);
    }
  }

  return { valid, errors };
}
